import { getService } from "./service";
import { asyncThunkWrapper } from "../utils/asyncThunkWrapper";

// Action type generator
export const createActionTypes = (entityName, tableName) => ({
  FETCH_REQUEST: `FETCH_${tableName}_REQUEST`,
  FETCH_SUCCESS: `FETCH_${tableName}_SUCCESS`,
  FETCH_FAILURE: `FETCH_${tableName}_FAILURE`,
  CREATE_REQUEST: `CREATE_${tableName}_REQUEST`,
  CREATE_SUCCESS: `CREATE_${tableName}_SUCCESS`,
  CREATE_FAILURE: `CREATE_${tableName}_FAILURE`,
  UPDATE_REQUEST: `UPDATE_${tableName}_REQUEST`,
  UPDATE_SUCCESS: `UPDATE_${tableName}_SUCCESS`,
  UPDATE_FAILURE: `UPDATE_${tableName}_FAILURE`,
  DELETE_REQUEST: `DELETE_${tableName}_REQUEST`,
  DELETE_SUCCESS: `DELETE_${tableName}_SUCCESS`,
  DELETE_FAILURE: `DELETE_${tableName}_FAILURE`,
});

// Main action factory
export const createServiceActions = (entityName, tableName, userAgency) => {
  const actionTypes = createActionTypes(entityName, tableName);

  const fetchData =
    (filters = "", page = 0, pageSize = 20, sortField = "code", sortDirection = "asc") =>
    async (dispatch) => {
      const service = getService(entityName);
      const query = {
        $limit: pageSize,
        $skip: page * pageSize,
        $sort: { [sortField]: sortDirection === "asc" ? 1 : -1 },
        isDeleted: false,
        agencyId: userAgency,
      };

      if (filters) {
        query.$or = [
          { code: { $like: `%${filters}%` } },
          { description: { $like: `%${filters}%` } },
        ];
      }

      return await asyncThunkWrapper({
        dispatch,
        types: {
          request: actionTypes.FETCH_REQUEST,
          success: actionTypes.FETCH_SUCCESS,
          failure: actionTypes.FETCH_FAILURE,
        },
        apiCall: () => service.find({ query }),
        transformResult: (res) => ({ data: res.data, total: res.total }),
        errorMessagePrefix: `Failed to fetch ${tableName}`,
      });
    };

  const createData = (data) => async (dispatch) => {
    const service = getService(entityName);

    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: actionTypes.CREATE_REQUEST,
        success: actionTypes.CREATE_SUCCESS,
        failure: actionTypes.CREATE_FAILURE,
      },
      apiCall: () => service.create(data),
      successMessage: `${tableName} created successfully`,
      errorMessagePrefix: `Failed to create ${tableName}`,
    });
  };

  const updateData = (id, data) => async (dispatch) => {
    const service = getService(entityName);

    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: actionTypes.UPDATE_REQUEST,
        success: actionTypes.UPDATE_SUCCESS,
        failure: actionTypes.UPDATE_FAILURE,
      },
      apiCall: () => service.patch(id, data),
      successMessage: `${tableName} updated successfully`,
      errorMessagePrefix: `Failed to update ${tableName}`,
    });
  };

  const deleteData = (id, userId) => async (dispatch) => {
    const service = getService(entityName);
    const patchData = {
      isDeleted: true,
      deletedBy: userId,
      deletedAt: new Date().toISOString(),
    };

    return await asyncThunkWrapper({
      dispatch,
      types: {
        request: actionTypes.DELETE_REQUEST,
        success: actionTypes.DELETE_SUCCESS,
        failure: actionTypes.DELETE_FAILURE,
      },
      apiCall: () => service.patch(id, patchData),
      transformResult: () => id,
      successMessage: `${tableName} deleted successfully`,
      errorMessagePrefix: `Failed to delete ${tableName}`,
    });
  };

  return {
    fetchData,
    createData,
    updateData,
    deleteData,
  };
};
